import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  FORM_THEMES, FormHeader, ShutdownInfo, PhotoGrid,
  ErrorBanner, FormBottomBar, captureGPS as doGPS, processPhoto
} from "./FormShared";

const theme = FORM_THEMES.mi;

export default function MIForm() {
  const { dtId } = useParams();
  const navigate = useNavigate();
  const [dt, setDT] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [dtmRecord, setDtmRecord] = useState(null);
  const [shutdowns, setShutdowns] = useState([]);
  const [serialMatch, setSerialMatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState("pending");
  const [gpsData, setGpsData] = useState({ lat: "", lng: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    meter_serial_verified: "", mrn_book_no: "", seal_serial: "",
    meter_display_reading: "", photos: [null, null, null]
  });

  const photoLabels = ["Meter Display", "Seal", "MRN Book"];

  useEffect(() => { api.get("/auth/me").then(r => setCurrentUser(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dtRes = await api.get(`/dt-master/${dtId}`);
        setDT(dtRes.data);
        const dtmRes = await api.get(`/installation/dtm/${dtId}`);
        setDtmRecord(dtmRes.data);
        const shutRes = await api.get("/shutdowns/available", { params: { feeder_code: dtRes.data.feeder_code } });
        setShutdowns(shutRes.data.items || []);
      } catch { navigate("/dt-master"); }
    };
    fetchData();
  }, [dtId]);

  const captureGPS = useCallback((silent = false) => doGPS(setGpsStatus, setGpsData, silent, toast), []);
  useEffect(() => { captureGPS(true); }, [captureGPS]);

  const checkSerialMatch = (serial) => {
    if (!dtmRecord) return;
    const match = serial.trim() === dtmRecord.meter_serial?.trim();
    setSerialMatch(match);
    if (match) toast.success("Serial verified — matches DTM record!");
    else if (serial) toast.error("Serial MISMATCH! Verify the meter.");
  };

  const handlePhotoUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const metadata = {
      capturedAt: new Date().toISOString(),
      capturedDate: new Date().toLocaleDateString("en-IN"),
      capturedTime: new Date().toLocaleTimeString("en-IN"),
      gps_lat: gpsData.lat || "N/A", gps_lng: gpsData.lng || "N/A",
      technicianName: currentUser?.full_name || "Unknown",
      label: `MI ${photoLabels[index]}`
    };
    const photoData = await processPhoto(file, metadata);
    const newPhotos = [...form.photos]; newPhotos[index] = photoData;
    setForm(f => ({ ...f, photos: newPhotos }));
    toast.success(`${photoLabels[index]} photo captured`);
  };

  const removePhoto = (index) => {
    const p = [...form.photos]; p[index] = null;
    setForm(f => ({ ...f, photos: p }));
  };

  const handleSubmit = async () => {
    if (!form.meter_serial_verified) { toast.error("Meter serial number is required"); return; }
    if (!serialMatch) { toast.error("Serial number mismatch! Verify before proceeding"); return; }
    if (!gpsData.lat) { toast.error("GPS location not captured. Please enable location services and tap 'Capture GPS'"); return; }
    if (!form.photos.some(p => p)) { toast.error("At least 1 photo is required"); return; }
    setLoading(true); setError(null);
    try {
      const photosData = form.photos.filter(p => p).map(p => ({
        url: `/mock/mi/${Date.now()}_${p.filename}`,
        metadata: { capturedAt: p.capturedAt, gps_lat: p.gps_lat, gps_lng: p.gps_lng, technician: p.technicianName, label: p.label }
      }));
      await api.post("/installation/mi", {
        dt_id: dtId,
        meter_serial_verified: form.meter_serial_verified,
        mrn_book_no: form.mrn_book_no || null,
        seal_serial: form.seal_serial || null,
        meter_display_reading: form.meter_display_reading ? parseFloat(form.meter_display_reading) : null,
        gps_lat: parseFloat(gpsData.lat), gps_lng: parseFloat(gpsData.lng),
        photos: photosData.map(p => p.url), photos_metadata: photosData
      });
      toast.success("MI completed! DT is now fully installed.");
      setSubmitted(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "MI submission failed";
      setError(msg); toast.error(msg, { duration: 10000 });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="mi-form">
      <FormHeader theme={theme} dt={dt} gpsStatus={gpsStatus} gpsData={gpsData}
        onGpsClick={() => captureGPS()} onBack={() => navigate(-1)} />

      {/* Sub-header: technician + DTM serial + shutdown info */}
      <div className="px-3 pt-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-slate-400">Technician: {currentUser?.full_name}</div>
          {dtmRecord && (
            <div className="text-[10px] text-slate-400">
              DTM Serial: <span className="font-mono font-bold text-slate-600">{dtmRecord.meter_serial}</span>
            </div>
          )}
        </div>
        <ShutdownInfo shutdowns={shutdowns} />
      </div>

      {/* Form Body */}
      <div className="flex-1 px-3 pt-3 pb-20 space-y-4">
        {/* Verify Meter Serial */}
        <div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-600 uppercase shrink-0 w-[72px]">Verify *</label>
            <div className="flex-1 relative">
              <input type="text" className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg font-mono bg-white pr-10"
                placeholder="Scan or enter meter serial"
                value={form.meter_serial_verified}
                onChange={e => { setForm({ ...form, meter_serial_verified: e.target.value }); setSerialMatch(null); }}
                onBlur={e => checkSerialMatch(e.target.value)}
                disabled={submitted} data-testid="mi-serial-input" />
              {serialMatch !== null && (
                <div className="absolute right-3 top-2.5">
                  {serialMatch ? <CheckCircle size={16} className="text-green-500" /> : <AlertCircle size={16} className="text-red-500" />}
                </div>
              )}
            </div>
          </div>
          {serialMatch === false && (
            <div className="ml-[84px] mt-1.5 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-[10px] flex items-center gap-1">
              <AlertCircle size={11} /> <strong>MISMATCH!</strong> Expected: <span className="font-mono">{dtmRecord?.meter_serial}</span>
            </div>
          )}
          {serialMatch === true && (
            <div className="ml-[84px] mt-1.5 p-1.5 bg-green-50 border border-green-200 rounded-lg text-green-600 text-[10px] flex items-center gap-1">
              <CheckCircle size={11} /> Serial verified
            </div>
          )}
        </div>

        {/* MRN / Seal / Reading */}
        <div className="space-y-2.5">
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-600 shrink-0 w-[72px]">MRN Book</label>
            <input type="text" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono bg-white"
              placeholder="MRN book number" value={form.mrn_book_no}
              onChange={e => setForm({ ...form, mrn_book_no: e.target.value })}
              disabled={submitted} data-testid="mrn-input" />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-600 shrink-0 w-[72px]">Seal No</label>
            <input type="text" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono bg-white"
              placeholder="Meter seal serial" value={form.seal_serial}
              onChange={e => setForm({ ...form, seal_serial: e.target.value })}
              disabled={submitted} data-testid="seal-serial-input" />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-600 shrink-0 w-[72px]">kWh</label>
            <input type="number" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
              placeholder="Current meter reading" value={form.meter_display_reading}
              onChange={e => setForm({ ...form, meter_display_reading: e.target.value })}
              disabled={submitted} data-testid="meter-reading-input" />
          </div>
        </div>

        {/* Photos */}
        <PhotoGrid photos={form.photos} photoLabels={photoLabels} onUpload={handlePhotoUpload}
          onRemove={removePhoto} submitted={submitted} theme={theme} testIdPrefix="mi" />

        <ErrorBanner error={error} testId="mi-error-banner" />
      </div>

      <FormBottomBar theme={theme} submitted={submitted} loading={loading}
        disabled={!form.meter_serial_verified || !serialMatch || !gpsData.lat}
        onSubmit={handleSubmit} dtId={dtId} navigate={navigate} />
    </div>
  );
}
